import { useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import 'react-resizable/css/styles.css';

import { Button, Flex, Typography } from 'antd';

import cloneDeep from 'lodash.clonedeep';

import { planHasFeature } from '../../../helpers/subscription';
import { hasPermission } from '../../../helpers/user';

import { FEATURES } from '../../../constants/features';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { fetchDashboardCounters } from '../../../redux/actions/dashboard';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchGrazingHeatmapStyles } from '../../../redux/actions/grazing';
import {
  createUserDashboardPreferences,
  deleteUserDashboardPreferences,
  fetchUserDashboardPreferences
} from '../../../redux/actions/userSlice';

import LoadingBar from '../../../components/LoadingBar';
import { errorToastHandler, successToastHandler } from '../../../components/action_notifier';

import AppDropdown from '../../ui-components/AppDropdown';

import styles from './styles.module.scss';

import CalendarWidget from './CalendarWidget';
import CardBody from './CardBody';
import CardHeader from './CardHeader';
import CountersList from './CountersList';
import Events from './Events';
import LivestockLocation from './LivestockLocation';
import WeatherWidget from './WeatherWidget';
import {
  BREAKPOINTS,
  BREEDING,
  COLS,
  HEALTH,
  HERD_METRICS,
  HERD_OVERVIEW,
  KEYS,
  LAYOUTS,
  LAYOUT_FIELDS_FOR_COMPARING,
  PASTURE,
  ROW_HEIGHT
} from './constants';
import { useDashboardMetrics } from './useDashboardMetrics';

const { Title } = Typography;
const ResponsiveGridLayout = WidthProvider(Responsive);

function areLayoutsEqualByFields(layout1, layout2, fields, idField = 'i') {
  if (!Array.isArray(layout1) || !Array.isArray(layout2)) return false;
  if (layout1.length !== layout2.length) return false;

  const map1 = new Map(layout1.map(item => [item[idField], item]));
  const map2 = new Map(layout2.map(item => [item[idField], item]));

  if (map1.size !== map2.size) return false;

  return Array.from(map1.entries()).every(([id, item1]) => {
    const item2 = map2.get(id);
    if (!item2) return false;

    return fields.every(field => item1[field] === item2[field]);
  });
}

function compareLayouts(layoutObj1, layoutObj2, fields, idField = 'i') {
  if (!layoutObj1 || !layoutObj2) return false;

  return Object.keys(layoutObj1).every(key => {
    const layout1 = layoutObj1[key] || [];
    const layout2 = layoutObj2[key] || [];

    return areLayoutsEqualByFields(layout1, layout2, fields, idField);
  });
}

const getLayouts = isHerdOverviewVisible => {
  const layouts = cloneDeep(LAYOUTS);

  if (!isHerdOverviewVisible) {
    ['lg', 'md', 'sm'].forEach(bp => {
      layouts[bp] = layouts[bp]
        .filter(l => l.i !== KEYS.HERD_OVERVIEW)
        .map(l => (l.i === KEYS.HERD_METRICS ? { ...l, w: 4, x: 0 } : l));
    });
  }

  return layouts;
};

function Dashboard() {
  const dispatch = useDispatch();

  const { counters: dashboardCounters } = useSelector(state => state.dashboard);
  const { myPlan } = useSelector(state => state.subscription);
  const { isAdmin, permissions } = useSelector(state => state.user);
  const { dashboardPreferences, loading } = useSelector(state => state.userSlice);

  const [allLayouts, setAllLayouts] = useState({});

  const isUserLayoutsSameToCurrent = compareLayouts(dashboardPreferences, allLayouts, LAYOUT_FIELDS_FOR_COMPARING);
  const isUserLayoutsSameToDefault = compareLayouts(dashboardPreferences, LAYOUTS, LAYOUT_FIELDS_FOR_COMPARING);
  const isCurrentLayoutsSameToDefault = compareLayouts(allLayouts, LAYOUTS, LAYOUT_FIELDS_FOR_COMPARING);
  const isSaveAllowed = dashboardPreferences ? !isUserLayoutsSameToCurrent : !isCurrentLayoutsSameToDefault;
  const isResetAllowed = dashboardPreferences ? !isUserLayoutsSameToDefault : !isCurrentLayoutsSameToDefault;

  const { herdMetrics, herdOverview, pasture, breeding, health } = useDashboardMetrics(dashboardCounters, {
    herdMetrics: HERD_METRICS,
    herdOverview: HERD_OVERVIEW,
    pasture: PASTURE,
    breeding: BREEDING,
    health: HEALTH
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setAllLayouts(dashboardPreferences ? dashboardPreferences : getLayouts(myPlan.type !== 'advanced'));
  }, [dashboardPreferences, myPlan]);

  const getFarms = async () => {
    if (!isAdmin && (!planHasFeature(myPlan, FEATURES.FARMS) || !hasPermission([PERMISSIONS.FARMS], permissions))) {
      return;
    }

    try {
      await dispatch(fetchFarms({ with_details: true })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  };

  async function fetchData() {
    try {
      await Promise.all([
        dispatch(fetchGrazingHeatmapStyles()),
        dispatch(fetchDashboardCounters()),
        dispatch(fetchUserDashboardPreferences()),
        getFarms()
      ]);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  function handleLayoutChange(currentLayout, allLayouts) {
    setAllLayouts(allLayouts);
  }

  async function handleSave() {
    try {
      await dispatch(createUserDashboardPreferences(allLayouts)).unwrap();
    } catch (error) {
      errorToastHandler(
        error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA('dashboard preferences')
      );
    }

    successToastHandler('Save successfully!');
  }

  async function handleReset() {
    try {
      await dispatch(deleteUserDashboardPreferences()).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }

    successToastHandler('Reset successfully!');
  }

  const actions = [
    {
      label: (
        <Button disabled={!isSaveAllowed} color="default" size="small" variant="link" onClick={handleSave}>
          Save
        </Button>
      )
    },
    {
      label: (
        <Button disabled={!isResetAllowed} color="default" size="small" variant="link" onClick={handleReset}>
          Reset
        </Button>
      )
    }
  ];

  const isHerdOverviewVisible = myPlan.type !== 'advanced';

  return (
    <>
      {loading && <LoadingBar fullscreen={true} />}
      {!loading && (
        <>
          <Flex className={styles.header} vertical={false} align="center" justify="space-between" gap="small">
            <Title level={4}>Dashboard</Title>
            <AppDropdown label="Actions" items={actions} />
          </Flex>

          <ResponsiveGridLayout
            className="layout"
            draggableHandle=".drag-handle"
            breakpoints={BREAKPOINTS}
            cols={COLS}
            layouts={allLayouts}
            rowHeight={ROW_HEIGHT}
            onLayoutChange={handleLayoutChange}>
            <div key={KEYS.HERD_METRICS} className={styles.card}>
              <CardHeader title="Herd Metrics" />
              <CardBody className="px-2 py-4">
                <CountersList data={herdMetrics} color="#e73a1e" />
              </CardBody>
            </div>

            {isHerdOverviewVisible && (
              <div key={KEYS.HERD_OVERVIEW} className={styles.card}>
                <CardHeader title="Herd Overview" />
                <CardBody className="px-2 py-4">
                  <CountersList data={herdOverview} color="red" />
                </CardBody>
              </div>
            )}

            <div key={KEYS.PASTURE} className={styles.card}>
              <CardHeader title="Pasture" />
              <CardBody className="px-2 py-4">
                <CountersList data={pasture} color="#e73a1e" />
              </CardBody>
            </div>

            <div key={KEYS.WEATHER} className={styles.card}>
              <CardHeader title="Weather" />
              <CardBody>
                <WeatherWidget />
              </CardBody>
            </div>

            <div key={KEYS.MAP} className={styles.card}>
              <CardHeader title="Farm Map" to="/maps" />
              <CardBody className="p-2">
                <LivestockLocation />
              </CardBody>
            </div>

            <div key={KEYS.BREEDING} className={styles.card}>
              <CardHeader title="Breeding Cycle" to="/breeding" />
              <CardBody className="px-2 py-4">
                <CountersList data={breeding} color="#e73a1e" />
              </CardBody>
            </div>

            {/*<div key="health" className={styles.card}>*/}
            {/*  <CardHeader title="Health & Welfare" />*/}
            {/*  <CardBody className="px-2 py-4">*/}
            {/*    <CountersList data={health} color="#e73a1e" />*/}
            {/*  </CardBody>*/}
            {/*</div>*/}

            <div key={KEYS.CALENDAR} className={styles.card}>
              <CardHeader title="Calendar" to="/planner" />
              <CardBody className="p-2">
                <CalendarWidget />
              </CardBody>
            </div>

            <div key={KEYS.EVENTS} className={styles.card}>
              <CardHeader title="Upcomming Events" to="/planner" />
              <CardBody className="px-2 py-4">
                <Events />
              </CardBody>
            </div>
          </ResponsiveGridLayout>
        </>
      )}
    </>
  );
}

export default Dashboard;
